import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Swal from "sweetalert2";
import { url } from "../../data/url";

const InternshipPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [departmentSearch, setDepartmentSearch] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs(activeTab);
  }, [activeTab, jobs, searchTerm, locationSearch, departmentSearch]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url.path}/employer/internship`, {
        withCredentials: true
      });
      const fetchedJobs = response.data.map(job => ({
        ...job,
        candidates: job.applicants.length,
        publishDate: formatDate(job.updatedAt)
      }));
      checkAndUpdateJobStatus(fetchedJobs);
      setJobs(fetchedJobs);
      setLoading(false);
    } catch (error) {
      if(error.response && error.response.status === 401) {
        navigate('/login');
      }
      console.error("Error fetching jobs:", error);
      setError('Error fetching jobs');
      setLoading(false);
    }
  };

  const checkAndUpdateJobStatus = (jobsToCheck) => {
    const today = new Date();
    jobsToCheck.forEach(async (job) => {

      if (job.activeTill && new Date(job.activeTill) < today && job.status !== "archive") {
        try {
          await axios.patch(`${url.path}/employer/internship/${job._id}/status`, { status: "archive" });
          fetchJobs();
        } catch (error) {
          console.error("Error updating job status:", error);
        }
      }
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  };

  const filterJobs = (status) => {
    let filtered = jobs;
    if (status !== "all") {
      filtered = filtered.filter((job) => job.status === status);
    }
    if (searchTerm) {
      filtered = filtered.filter((job) =>
        job.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (locationSearch) {
      filtered = filtered.filter((job) =>
        job.jobLocation.toLowerCase().includes(locationSearch.toLowerCase())
      );
    }
    if (departmentSearch) {
      filtered = filtered.filter((job) =>
        job.department.toLowerCase().includes(departmentSearch.toLowerCase())
      );
    }
    setFilteredJobs(filtered);
  };

  const handleTabClick = (status) => {
    setActiveTab(status);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const getJobCount = (status) => {
    return jobs.filter((job) => job.status === status).length;
  };

  const toggleDropdown = (jobId) => {
    setActiveDropdown(activeDropdown === jobId ? null : jobId);
  };

  const handlePostJob = async (job) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to mark this job as active?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, post it!"
    });

    if (result.isConfirmed) {
      try {
        await axios.patch(`${url.path}/employer/internship/${job._id}/status`, { status: "active" });
        fetchJobs();
        Swal.fire("Posted!", "The job has been marked as active.", "success");
      } catch (error) {
        console.error("Error posting job:", error);
        Swal.fire("Error", "There was an error posting the job.", "error");
      }
    }
  };


  const handleEditJob = (job) => {
    navigate(`/employer/edit-job/${job._id}`);
  };

  const handlePreviewJob = (job) => {
    navigate(`/employer/internship/${job._id}/description`);
  };

  const handleDeleteJob = async (job) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${url.path}/employer/internship/${job._id}`);
        fetchJobs();
        Swal.fire("Deleted!", "The job has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting job:", error);
        Swal.fire("Error", "There was an error deleting the job.", "error");
      }
    }
  };

  const renderTableRow = (job) => (
    <tr key={job._id}>
      <td className="job-title">{job.jobTitle}</td>
      <td>{job.jobLocation}</td>
      <td>{job.department}</td>
      <td>
        <div className="candidates-cell">
          <span className="candidate-count">
            {(job.status.toLowerCase() !== "draft" && job.candidates) || "-"}
          </span>
          {/* {job.status.toLowerCase() !== "draft" && job.offers && (
            <button className="offer-count">
              {job.offers} OFFER{job.offers > 1 ? "S" : ""}
            </button>
          )} */}
        </div>
      </td>
      <td>
        <span className={`status ${job.status}`}>
          {job.status.toUpperCase()}
        </span>
      </td>
      <td>{job.publishDate}</td>
      <td>
        <div className="dropdown" onMouseLeave={() => setActiveDropdown(null)}>
          <button className="manage-button" onClick={() => toggleDropdown(job._id)}>
            •••
          </button>
          {activeDropdown === job._id && (
            <div className="dropdown-menu upward">
              <button className="dropdown-item" onClick={() => handleEditJob(job)}>
                <span className="icon">✏️</span> Edit job
              </button>
              <button className="dropdown-item" onClick={() => handlePreviewJob(job)}>
                <span className="icon">👁️</span> Preview job
              </button>
              <button className="dropdown-item" onClick={() => handleDeleteJob(job)}>
                <span className="icon">🗑️</span> Permanently delete
              </button>
              {job.status.toUpperCase() === "DRAFT" && (
                <button className="dropdown-item" onClick={() => handlePostJob(job)}>
                  <span className="icon">⬆️</span> Post Job
                </button>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="hiring-container">
      <div className="hiring-page">
        <header className="hiring-header">
          <div className="internship-header">
          <h1>Internships</h1>
          </div>
          <div className="header-right">
            <input
              type="text"
              placeholder="Search an internship..."
              value={searchTerm}
              onChange={handleSearch}
            />
            <Link to="/employer/post-job" className="add-job-btn">
              <FontAwesomeIcon icon={faPlus} className="icon" />
              <span>New Internship</span>
            </Link>
          </div>
        </header>
        <div className="jobs-tab-container">
          <div className="job-tabs">
            <div className="jobs-tabs-filters">
              <button
                className={activeTab === "all" ? "active" : ""}
                onClick={() => handleTabClick("all")}
              >
                All ({jobs.length})
              </button>
              <button
                className={activeTab === "active" ? "active" : ""}
                onClick={() => handleTabClick("active")}
              >
                Active ({getJobCount("active")})
              </button>
              <button
                className={activeTab === "draft" ? "active" : ""}
                onClick={() => handleTabClick("draft")}
              >
                Draft ({getJobCount("draft")})
              </button>
              <button
                className={activeTab === "archive" ? "active" : ""}
                onClick={() => handleTabClick("archive")}
              >
                Archived ({getJobCount("archive")})
              </button>
            </div>
          </div>
          <div className="filters">
            <input
              type="text"
              placeholder="Search by location ▼"
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
            />
            <input
              type="text"
              placeholder="Search by department ▼"
              value={departmentSearch}
              onChange={(e) => setDepartmentSearch(e.target.value)}
            />
          </div>
          <div className="intership-table">
          <table>
            <thead>
              <tr>
                <th>TITLE</th>
                <th>LOCATION</th>
                <th>DEPARTMENT</th>
                <th>CANDIDATES</th>
                <th>STATUS</th>
                <th>PUBLISH DATE</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.length > 0 ? (filteredJobs.map(renderTableRow))
              : <div className="internship-placeholder">
              No internship posted yet
            </div>}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternshipPage;
